/* Batch simulation — "how does my deck stack up against the field?"
 *
 * This is the whole reason the engine is headless. Nothing here touches the
 * DOM or a timer, so a few thousand games run in a fraction of a second and the
 * result is reproducible from the seed.
 *
 * Every matchup is played on BOTH sides of the die: each seed is run once with
 * the deck on the play and once on the draw. Going first is a real advantage in
 * this game, so a one-sided sample would flatter whichever deck got it.
 */

import { createGame, apply } from './engine.js';
import { decide, SKILL } from './ai.js';

const MAX_ACTIONS = 4000;   // a game this long is a stalled state, not a real one

/**
 * Whose decision the engine is waiting on.
 *
 * Not always the active player: a pending prompt (block, counter, a mode
 * choice) belongs to whoever has to answer it, which during a counter step is
 * the defender. Driving both sides from `G.active` would hand the attacker the
 * defender's decisions.
 */
export const actingSide = G => (G.pending ? G.pending.side : G.active);

/**
 * Play one game to completion. Returns the winner and why it ended.
 *
 * `policies` lets each seat run a different decision function, which is what
 * makes one policy measurable against another — with a single shared `skill`
 * every game is a mirror and the only thing a win rate can report is deck
 * strength. Omit it and both seats use `skill`, as before.
 *
 * @param {{p1: (G)=>object, p2: (G)=>object}} [policies]
 */
export function playGame({ p1, p2, cards, seed, firstPlayer = 'p1', skill = SKILL.solid, policies = null }) {
  const G = createGame({ p1, p2, cards, seed, firstPlayer });
  const choose = policies
    ? g => policies[actingSide(g)](g)
    : g => decide(g, skill);

  let n = 0;
  while (!G.over && n++ < MAX_ACTIONS) {
    const action = choose(G);
    if (!action) break;
    apply(G, action);
  }
  return {
    winner: G.over?.winner ?? null,
    reason: G.over?.reason ?? 'game did not resolve',
    turns: G.turn,
    stalled: !G.over,
  };
}

/**
 * Seed and seat for game `i` of a matchup.
 *
 * Games are paired: `i` and `i+1` share a seed and swap seats, so the same
 * shuffle is played from both sides. Exported because that pairing is the
 * property worth testing and it is otherwise invisible from outside — the
 * regression it replaces (a seed that advanced every game) produced a
 * perfectly plausible-looking win rate.
 */
export const gameSchedule = (seed, i) => ({
  seed: seed + (i >> 1),
  firstPlayer: i % 2 === 0 ? 'p1' : 'p2',
});

/**
 * Run `games` matches between two decks, playing each shuffle from both sides.
 *
 * Games come in pairs that share a seed: the same shuffles and opening hands
 * are played once with the deck on the play and once on the draw. That is the
 * variance reduction the A/B workflow depends on — change two cards, re-run
 * against the same field and seed, and the difference between the runs is
 * signal rather than a different set of opening hands.
 *
 * It used to advance the seed on every game (`seed + i`) while flipping the
 * first player on the same counter, so no two games ever shared a shuffle and
 * the pairing described here never happened.
 *
 * An odd `games` count leaves the final pair half-played, which skews the
 * play/draw split by one game; prefer an even number.
 *
 * @returns {{wins,losses,games,played,winRate,onPlay,onDraw,avgTurns,stalled,ci95}}
 */
export function runMatchup({ deck, opponent, cards, games = 200, seed = 1, skill = SKILL.solid }) {
  let wins = 0, stalled = 0, turnSum = 0;
  const onPlay = { wins: 0, games: 0 };
  const onDraw = { wins: 0, games: 0 };

  for (let i = 0; i < games; i++) {
    const { seed: gameSeed, firstPlayer } = gameSchedule(seed, i);
    const r = playGame({
      p1: { name: 'deck', leaderId: deck.leader, deck: deck.cards },
      p2: { name: 'opponent', leaderId: opponent.leader, deck: opponent.cards },
      cards,
      seed: gameSeed,
      firstPlayer,
      skill,
    });
    const bucket = firstPlayer === 'p1' ? onPlay : onDraw;
    bucket.games++;
    turnSum += r.turns;
    if (r.stalled) stalled++;
    if (r.winner === 'p1') { wins++; bucket.wins++; }
  }

  const played = games - stalled;
  const winRate = played ? wins / played : 0;

  return {
    // `played` excludes stalled games, which is what winRate and the interval
    // are computed over. There is no draw in this game, so there is no `draws`.
    games, played, wins, losses: played - wins, stalled,
    winRate,
    ci95: wilson95(wins, played),
    onPlay: onPlay.games ? onPlay.wins / onPlay.games : 0,
    onDraw: onDraw.games ? onDraw.wins / onDraw.games : 0,
    avgTurns: games ? turnSum / games : 0,
  };
}

/**
 * Play one deck against every archetype in the field.
 * `field` is `[{ key, name, leader, cards, share }]`.
 */
export function runGauntlet({ deck, field, cards, games = 200, seed = 1, skill = SKILL.solid, onProgress }) {
  const rows = [];
  for (let i = 0; i < field.length; i++) {
    const opp = field[i];
    const r = runMatchup({ deck, opponent: opp, cards, games, seed, skill });
    rows.push({ key: opp.key, name: opp.name, leader: opp.leader, share: opp.share ?? null, ...r });
    onProgress?.(i + 1, field.length, rows[rows.length - 1]);
  }

  return { ...summariseField(rows), games };
}

/**
 * The two headline numbers, from a set of matchup rows.
 *
 * Separate from `runGauntlet` because the field can be played in pieces: the
 * server splits it across simulation threads (#96) and has to combine the
 * halves without a second implementation of this arithmetic. Every matchup is
 * seeded from `seed` alone, not from its position in the field, so a row is
 * the same row whichever slice produced it — which is what makes recombining
 * them legitimate rather than merely convenient.
 *
 * @param {object[]} rows
 * @returns {{rows: object[], overall: number, fieldWeighted: number}}
 */
export function summariseField(rows) {
  // Weight by how much of the field each archetype actually represents, so a
  // 25%-share matchup counts for more than a 1% one.
  const totalShare = rows.reduce((a, r) => a + (r.share || 0), 0);
  const weighted = totalShare > 0
    ? rows.reduce((a, r) => a + r.winRate * (r.share || 0), 0) / totalShare
    : rows.reduce((a, r) => a + r.winRate, 0) / (rows.length || 1);

  return {
    rows: rows.slice().sort((a, b) => b.winRate - a.winRate),
    overall: rows.length ? rows.reduce((a, r) => a + r.winRate, 0) / rows.length : 0,
    fieldWeighted: weighted,
  };
}

/* ---------------------------------------------------------------------- */
/* policy benchmarking                                                     */
/* ---------------------------------------------------------------------- */

/**
 * Play policy A against policy B and report A's win rate.
 *
 * "Better AI" is unfalsifiable without this. A gauntlet answers "is this deck
 * good", with both seats running the same policy, so the policy cancels out and
 * cannot be read off the result at all.
 *
 * Three confounds have to be removed, or the number measures something else:
 *
 *   Seat — going first is a real advantage. Every seed is played twice with the
 *   seats swapped, exactly as `runMatchup` does.
 *
 *   Deck — a policy handed the stronger list wins on the list. Every deck
 *   pairing is therefore played twice more, with the policies swapped between
 *   the two decks, so deck strength appears on both sides of the ledger and
 *   cancels. This is why `decks` is a list and one deck is not enough.
 *
 *   Mirror — passing the same policy as both A and B must land on 50%. That is
 *   the harness's own control: if a mirror comes out lopsided, the pairing is
 *   broken and no other number from it means anything.
 *
 * @param {object} o
 * @param {(G)=>object} o.a          policy under test
 * @param {(G)=>object} o.b          reference policy
 * @param {Array<{name,leader,cards}>} o.decks  at least one; pairings are drawn from these
 * @param {object} o.cards
 * @param {number} [o.games]  games per (deck-pair, orientation). Rounded up to a multiple of 2.
 * @returns {{winRate, ci95, wins, played, stalled, rows}}
 */
export function runPolicyDuel({ a, b, decks, cards, games = 100, seed = 1 }) {
  if (!decks?.length) throw new Error('runPolicyDuel needs at least one deck');

  /* Every ordered pair, including a deck against itself. The self-pairing is
   * the cleanest signal available — identical lists, so nothing but the policy
   * differs — and the cross pairings stop that from being the only shape of
   * game the policies are ever compared on. */
  const pairs = [];
  for (const x of decks) for (const y of decks) pairs.push([x, y]);

  const perOrientation = Math.max(2, games - (games % 2));
  let wins = 0, played = 0, stalled = 0;
  const rows = [];

  for (const [dx, dy] of pairs) {
    let pairWins = 0, pairPlayed = 0;

    /* Orientation 0: A pilots dx, B pilots dy. Orientation 1 swaps the decks
     * between the policies, so any strength difference between dx and dy is
     * credited to both policies equally.
     *
     * A deck against itself has nothing to swap — both orientations would
     * replay the identical games. Running it twice would double-weight the
     * self-pairings and, worse, inflate `played` with duplicates, reporting an
     * interval narrower than the evidence supports. */
    const orientations = dx === dy ? 1 : 2;
    for (let orientation = 0; orientation < orientations; orientation++) {
      const aDeck = orientation === 0 ? dx : dy;
      const bDeck = orientation === 0 ? dy : dx;

      for (let i = 0; i < perOrientation; i++) {
        const { seed: gameSeed, firstPlayer } = gameSchedule(seed, i);
        const r = playGame({
          p1: { name: 'A', leaderId: aDeck.leader, deck: aDeck.cards },
          p2: { name: 'B', leaderId: bDeck.leader, deck: bDeck.cards },
          cards,
          seed: gameSeed,
          firstPlayer,
          policies: { p1: a, p2: b },
        });

        if (r.stalled) { stalled++; continue; }
        played++; pairPlayed++;
        if (r.winner === 'p1') { wins++; pairWins++; }
      }
    }

    rows.push({
      a: dx.name, b: dy.name,
      wins: pairWins, played: pairPlayed,
      winRate: pairPlayed ? pairWins / pairPlayed : 0,
    });
  }

  return {
    wins, played, stalled,
    winRate: played ? wins / played : 0,
    ci95: wilson95(wins, played),
    rows,
  };
}

/* Wilson score interval — honest error bars on a proportion at small N.
 * A naive ±1/sqrt(n) badly understates uncertainty near 0% or 100%. */
export function wilson95(wins, n) {
  if (!n) return [0, 0];
  const z = 1.959964;
  const p = wins / n;
  const d = 1 + z * z / n;
  const c = p + z * z / (2 * n);
  const s = z * Math.sqrt(p * (1 - p) / n + z * z / (4 * n * n));
  return [Math.max(0, (c - s) / d), Math.min(1, (c + s) / d)];
}
